import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react"
import { useNavigate } from "react-router-dom"
import type { User } from "@supabase/supabase-js"
import { supabase } from "@/lib/supabase/client"
import { isMockMode, MOCK_USER, MOCK_PROFILE } from "@/lib/mock/mock-supabase"
import { MOCK_TENANTS } from "@/lib/mock/mock-data"
import { escopoAoAbrir, escopoPedido, unidadeUnica } from "@/lib/escopoDaUnidade"
import { juntarUnidadesLidas } from "@/lib/leituraDaRede"
import type {
  Tenant,
  Profile,
  AppRole,
  TenantContextValue,
  PermissionAction,
} from "@/types/index"
import { ROLE_HIERARCHY } from "@/types/index"

// ─── Mapa de permissoes por role ───────────────────────────────────────────────
const PERMISSION_MAP: Record<PermissionAction, AppRole[]> = {
  view_funil: ["super_admin", "network_manager", "tenant_admin", "backoffice", "seller"],
  manage_esc_settings: ["super_admin", "network_manager", "tenant_admin"],
  create_esc: ["super_admin", "network_manager", "tenant_admin"],
  invite_users: ["super_admin", "network_manager", "tenant_admin"],
  view_super_admin: ["super_admin"],
  access_ai_config: ["super_admin", "network_manager", "tenant_admin", "ad_agency"],
  manage_leads: ["super_admin", "network_manager", "tenant_admin", "backoffice"],
  view_all_leads: ["super_admin", "network_manager", "tenant_admin", "backoffice"],
  god_mode: ["super_admin"],
}

// ─── Context ───────────────────────────────────────────────────────────────────
export const TenantContext = createContext<TenantContextValue | null>(null)

/**
 * A unidade pedida no endereço (`?unidade=`), quando a pessoa tem acesso a ela.
 *
 * 🚨 POR QUE EXISTE. Os relatórios por e-mail trazem números de UMA unidade e um
 * link para a tela correspondente. Sem isto, quem administra várias unidades — o
 * Michel administra 7 — clica num número da Braga e cai na unidade que ficou
 * salva da última visita, lendo outro número com o mesmo rótulo. O link mentiria
 * calado.
 *
 * Aceita o identificador ou o apelido da unidade, para o link poder ser legível.
 *
 * ⚠️ Unidade que a pessoa não alcança devolve `null` e o fluxo cai no
 * comportamento normal. Não erra na tela e não confirma que a unidade existe —
 * quem decide o que cada pessoa vê continua sendo a RLS, e um link colado de
 * outra pessoa não pode virar uma pista.
 */
export function unidadeDoEndereco(busca: string, disponiveis: Tenant[]): Tenant | null {
  const pedida = new URLSearchParams(busca).get("unidade")?.trim()
  if (!pedida) return null
  return (
    disponiveis.find((t) => t.id === pedida) ??
    disponiveis.find((t) => (t.slug ?? "").toLowerCase() === pedida.toLowerCase()) ??
    null
  )
}

function unidadePedidaNoEndereco(disponiveis: Tenant[]): Tenant | null {
  try {
    return unidadeDoEndereco(window.location.search, disponiveis)
  } catch {
    return null
  }
}

/**
 * As unidades que a pessoa lê sem vínculo (migration 293). Para quase todo
 * mundo o banco devolve []. Nunca rejeita: erro, rede fora ou função ainda não
 * aplicada viram `null`, e `juntarUnidadesLidas` deixa a pessoa só com os
 * vínculos de sempre. Rejeitar aqui derrubaria o Promise.all da carga inicial
 * e a pessoa ficaria sem unidade nenhuma.
 */
async function unidadesLidas(): Promise<unknown> {
  try {
    const { data, error } = await supabase.rpc("unidades_da_leitura")
    return error ? null : data
  } catch {
    return null
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export function TenantProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()

  // Estado de autenticacao
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [role, setRole] = useState<AppRole>("seller")
  const [isLoading, setIsLoading] = useState(true)
  const [isDataReady, setIsDataReady] = useState(false)

  // Estado de tenants
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [currentTenant, setCurrentTenantState] = useState<Tenant | null>(null)

  // Escopo global (seletor de unidade). Declarado aqui, junto do resto do
  // estado, porque `initializeUser` precisa restaurar a unidade salva ANTES de
  // liberar as telas — ver comentário lá.
  const [selectedScope, setSelectedScopeRaw] = useState<string>("all")
  const scopeRestoredRef = useRef(false)

  // Erro de carregamento (safety timer expirou)
  const [hasLoadError, setHasLoadError] = useState(false)

  // God Mode (Super Admin visualizando uma ESC)
  const [isInGodMode, setIsInGodMode] = useState(false)
  const [godModeTenant, setGodModeTenant] = useState<Tenant | null>(null)

  // Ref para evitar re-execucao do fluxo de inicializacao
  const initializedRef = useRef(false)
  // Ref para rastrear se loadUserData está em execução (previne race com TOKEN_REFRESHED)
  const loadingDataRef = useRef(false)

  /**
   * Busca perfil, role e tenants do usuario autenticado.
   * Executado apos cada mudanca de estado de autenticacao.
   */
  const loadUserData = useCallback(async (authUser: User) => {
    // ── Mock mode: retorna dados mockados sem tocar no Supabase ──────────────
    if (isMockMode) {
      setProfile(MOCK_PROFILE)
      const userRole: AppRole = "super_admin"
      setRole(userRole)
      setTenants(MOCK_TENANTS)
      return { userRole, userTenants: MOCK_TENANTS }
    }

    try {
      // Buscar perfil e role em paralelo (sem timeout — safety timer de 15s protege)
      const [profileResult, roleResult, tenantsResult, leituraResult] = await Promise.all([
        supabase
          .from("credit_v2_profiles")
          .select("*")
          .eq("id", authUser.id)
          .maybeSingle(),
        supabase
          .from("credit_v2_user_roles")
          .select("role")
          .eq("user_id", authUser.id),
        supabase
          .from("credit_v2_user_tenants")
          .select("*, tenant:credit_v2_tenants(*)")
          .eq("user_id", authUser.id),
        unidadesLidas(),
      ])

      if (profileResult.data) {
        setProfile(profileResult.data as Profile)
      }

      // Pick the highest role when user has multiple role records (one per tenant)
      const roles = (roleResult.data ?? []).map((r: { role: string }) => r.role as AppRole)
      const userRole = roles.length > 0
        ? roles.reduce((best: AppRole, r: AppRole) => ROLE_HIERARCHY[r] > ROLE_HIERARCHY[best] ? r : best)
        : "seller" as AppRole
      setRole(userRole)

      const vinculadas = (tenantsResult.data ?? [])
        .map((ut: { tenant: Tenant | null }) => ut.tenant)
        .filter((t: Tenant | null): t is Tenant => t !== null)
        // #222: ocultar ESCs desativadas do seletor e de todas as views normais.
        // A pagina de gerenciamento (super-admin) carrega tenants separadamente.
        .filter((t: Tenant) => !t.deactivated_at)
      // Migration 293: as unidades que a pessoa só lê vêm depois dos vínculos.
      const comLeitura = juntarUnidadesLidas(vinculadas, leituraResult)
      setTenants(comLeitura)

      return { userRole, userTenants: comLeitura }
    } catch (error) {
      console.error("Erro ao carregar dados do usuario:", error)
      return { userRole: "seller" as AppRole, userTenants: [] }
    }
  }, [])

  /**
   * Fluxo de inicializacao centralizado.
   * Carrega perfil, role, tenants e auto-seleciona o primeiro tenant.
   */
  const initializeUser = useCallback(
    async (authUser: User) => {
      if (loadingDataRef.current) return
      loadingDataRef.current = true
      setIsLoading(true)

      try {
        const { userTenants } = await loadUserData(authUser)
        if (userTenants.length > 0) {
          // Decide o escopo AQUI, antes de `setIsDataReady(true)` no
          // finally. As telas só consultam depois que isDataReady liga; se a
          // decisão ficasse num efeito posterior (como ficava), a primeira
          // consulta de cada tela sairia com escopo "all" e a resposta dela —
          // maior e mais lenta — chegaria depois da correta e a sobrescreveria.
          // Sintoma: KPIs e listas da rede inteira sob o nome de uma unidade.
          //
          // A ORDEM mora em `escopoAoAbrir` (`@/lib/escopoDaUnidade`). O
          // endereço manda mais que o que ficou salvo: quem clicou num link com
          // `?unidade=` está dizendo qual unidade quer ver agora, e a
          // preferência guardada é só a memória da última visita. Sem nenhum
          // dos dois, quem tem uma unidade só entra direto nela, porque para
          // essa pessoa não existe seletor no topo para sair de "Todas as
          // unidades". O resto abre em "Todas as unidades".
          //
          // A decisão mora AQUI, no mesmo lugar que já escolhia o escopo, e não
          // num efeito à parte. Um efeito paralelo mexendo no escopo foi
          // exatamente o defeito que a nota acima descreve — e a lição vale
          // dobrado para endereço: parâmetro de URL tem um dono só.
          let guardada: string | null = null
          try {
            guardada = localStorage.getItem(`creditos_scope_${authUser.id}`)
          } catch { /* localStorage indisponível: decide sem a memória */ }
          const decidido = escopoAoAbrir(userTenants, unidadePedidaNoEndereco(userTenants), guardada)

          scopeRestoredRef.current = true
          if (decidido.unidade) {
            setSelectedScopeRaw(decidido.escopo)
            setCurrentTenantState(decidido.unidade)
            // Guarda a escolha do link: navegar para outra tela depois não pode
            // devolver a pessoa para a unidade anterior no meio da leitura. A
            // unidade de quem tem uma só não é gravada: não foi escolha.
            if (decidido.gravar) {
              try {
                localStorage.setItem(`creditos_scope_${authUser.id}`, decidido.escopo)
              } catch { /* sem localStorage o escopo vale só nesta tela */ }
            }
          } else {
            setCurrentTenantState((prev) => prev ?? userTenants[0])
          }
        }
      } catch (err) {
        console.error("Erro ao inicializar dados do usuário:", err)
      } finally {
        loadingDataRef.current = false
        setIsDataReady(true)
        setIsLoading(false)
      }
    },
    [loadUserData],
  )

  // Inicializar: escutar mudancas de estado de autenticacao
  useEffect(() => {
    if (isMockMode) {
      const timer = setTimeout(async () => {
        const mockUserAsUser = MOCK_USER as unknown as User
        setUser(mockUserAsUser)
        initializedRef.current = true
        setIsLoading(true)
        await loadUserData(mockUserAsUser)
        setIsDataReady(true)
        setIsLoading(false)
      }, 100)
      return () => clearTimeout(timer)
    }

    // ── Modo real ───────────────────────────────────────────────────────
    // Safety timeout: se auth/dados não carregarem em 5s, liberar UI
    const safetyTimer = setTimeout(() => {
      if (!initializedRef.current) {
        console.warn("Auth timeout (5s) — liberando UI com erro")
        setHasLoadError(true)
        setIsLoading(false)
        setIsDataReady(true)
      }
    }, 5000)

    // 1. Inicializar imediatamente da sessão local (sem chamada de rede)
    //    getSession() lê do localStorage — é instantâneo.
    supabase.auth.getSession().then(
      ({ data: { session } }: { data: { session: { user: User } | null } }) => {
        if (session?.user && !initializedRef.current) {
          initializedRef.current = true
          setUser(session.user)
          void initializeUser(session.user).then(() => clearTimeout(safetyTimer))
        } else if (!session) {
          // Não logado — liberar UI imediatamente
          setIsLoading(false)
          setIsDataReady(true)
          clearTimeout(safetyTimer)
        }
      },
    )

    // 2. Escutar mudanças de auth (SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: string, session: { user: User } | null) => {
      // INITIAL_SESSION já tratado pelo getSession() acima — ignorar
      if (event === "INITIAL_SESSION") return

      if (event === "SIGNED_OUT" || !session) {
        // Clear persisted scope for this user before wiping the user ref
        setUser((prev) => {
          if (prev) localStorage.removeItem(`creditos_scope_${prev.id}`)
          return null
        })
        setProfile(null)
        setRole("seller")
        setTenants([])
        setCurrentTenantState(null)
        setSelectedScopeRaw("all")
        scopeRestoredRef.current = false
        setIsInGodMode(false)
        setGodModeTenant(null)
        setIsDataReady(false)
        setIsLoading(false)
        clearTimeout(safetyTimer)
        initializedRef.current = false
        return
      }

      setUser(session.user)

      if (event === "SIGNED_IN" && !initializedRef.current) {
        // Novo login (não é restauração de sessão)
        initializedRef.current = true
        void initializeUser(session.user).then(() => clearTimeout(safetyTimer))
      } else if (event === "TOKEN_REFRESHED" && !loadingDataRef.current) {
        // Token renovado — recarregar dados se o load inicial falhou (tenants vazio)
        setTenants((prev) => {
          if (prev.length === 0 && session.user) {
            // Disparar reload em background (sem bloquear UI)
            void initializeUser(session.user)
          }
          return prev
        })
      }
    })

    return () => {
      clearTimeout(safetyTimer)
      subscription.unsubscribe()
    }
  }, [loadUserData, initializeUser])

  const setCurrentTenant = useCallback((tenant: Tenant) => {
    setCurrentTenantState(tenant)
    setTenants(prev => prev.map(t => t.id === tenant.id ? tenant : t))
  }, [])

  /**
   * God Mode: super_admin acessa uma ESC especifica.
   * Um banner amarelo persistente e exibido durante o God Mode.
   * Todas as acoes em God Mode sao registradas em credit_v2_usage_logs.
   */
  const enterGodMode = useCallback(
    (tenant: Tenant, redirectTo: string) => {
      setIsInGodMode(true)
      setGodModeTenant(tenant)
      setCurrentTenantState(tenant)
      navigate(redirectTo)
    },
    [navigate],
  )

  const exitGodMode = useCallback(() => {
    setIsInGodMode(false)
    setGodModeTenant(null)
    setCurrentTenantState(null)
    navigate("/super-admin")
  }, [navigate])

  /**
   * Recarrega apenas o perfil do usuário atual (sem refazer role/tenants).
   * Usado após alterações locais em credit_v2_profiles (avatar, nome, preferências).
   */
  const refreshProfile = useCallback(async () => {
    if (isMockMode) {
      setProfile(MOCK_PROFILE)
      return
    }
    if (!user) return
    const { data } = await supabase
      .from("credit_v2_profiles")
      .select("*")
      .eq("id", user.id)
      .maybeSingle()
    if (data) setProfile(data as Profile)
  }, [user])

  const signOut = useCallback(async () => {
    if (isMockMode) {
      // Em mock mode, apenas limpa o estado local
      setUser(null)
      setProfile(null)
      setRole("seller")
      setTenants([])
      setCurrentTenantState(null)
      initializedRef.current = false
      navigate("/login")
      return
    }
    await supabase.auth.signOut()
    navigate("/login")
  }, [navigate])

  /** Verifica se o usuario tem permissao para uma acao especifica */
  const can = useCallback(
    (action: PermissionAction): boolean => {
      const allowedRoles = PERMISSION_MAP[action]
      return allowedRoles.includes(role)
    },
    [role],
  )

  /** Verifica se o usuario tem exatamente um role especifico */
  const hasRole = useCallback(
    (targetRole: AppRole): boolean => {
      return role === targetRole
    },
    [role],
  )

  /** Verifica se o usuario tem pelo menos um nivel de role */
  const isAtLeast = useCallback(
    (minRole: AppRole): boolean => {
      return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[minRole]
    },
    [role],
  )

  const retryLoad = useCallback(() => {
    window.location.reload()
  }, [])

  // ── Global scope (unit selector) ────────────────────────────────────
  // O estado e o ref moram lá em cima, junto do resto — `initializeUser` os usa.

  // Retaguarda da decisão do escopo: o caminho normal já decidiu dentro de
  // `initializeUser` (e marcou o ref), então este efeito não roda. Ele cobre os
  // caminhos que não passam por lá — modo mock e reidratação por troca de sessão.
  // Mesma regra e mesma ordem do caminho principal (`escopoAoAbrir`): endereço,
  // unidade salva ainda válida, unidade única e, no resto, "Todas as unidades".
  useEffect(() => {
    if (!isDataReady || !user || tenants.length === 0) return
    if (scopeRestoredRef.current) return
    scopeRestoredRef.current = true

    const key = `creditos_scope_${user.id}`
    let guardada: string | null = null
    try { guardada = localStorage.getItem(key) } catch { /* sem localStorage */ }
    const decidido = escopoAoAbrir(tenants, unidadePedidaNoEndereco(tenants), guardada)
    // "Todas as unidades": nada a aplicar, igual ao caminho principal.
    if (!decidido.unidade) return

    setSelectedScopeRaw(decidido.escopo)
    setCurrentTenantState(decidido.unidade)
    if (decidido.gravar) {
      try { localStorage.setItem(key, decidido.escopo) } catch { /* sem localStorage */ }
    }
  }, [isDataReady, user, tenants])

  // When selecting a specific tenant, also update currentTenant so pages using it react.
  // Also persist the choice so it survives navigation and refresh.
  //
  // 🚨 A invariante não depende de quem chama: com uma unidade visível só, o
  // pedido de "Todas as unidades" (ou de qualquer outra coisa) fica na unidade
  // e nada é gravado. Ver `escopoPedido` em `@/lib/escopoDaUnidade`.
  const setSelectedScope = useCallback((scope: string) => {
    const decidido = escopoPedido(scope, tenants)
    setSelectedScopeRaw(decidido.escopo)
    if (decidido.unidade) setCurrentTenantState(decidido.unidade)
    if (user && decidido.gravar) {
      localStorage.setItem(`creditos_scope_${user.id}`, decidido.escopo)
    }
  }, [tenants, user])

  /**
   * Recarrega a lista de ESCs do usuário sem exigir reload da página.
   * Chamado após criar uma ESC (nova-esc) para que a nova unidade apareça no
   * menu na hora e o seletor de unidades surja quando vira a 2ª ESC. Se
   * `selectTenantId` for passado (ex.: a ESC recém-criada), foca ela como
   * escopo ativo pra o usuário já cair pronto pra gerenciá-la.
   *
   * A lista recarregada passa pela mesma regra do escopo
   * (`@/lib/escopoDaUnidade`): com uma unidade visível só, o escopo é ela, com
   * ou sem `selectTenantId`, e nada é gravado.
   */
  const refreshTenants = useCallback(async (selectTenantId?: string) => {
    if (isMockMode) {
      setTenants(MOCK_TENANTS)
      return
    }
    if (!user) return

    const [{ data }, leitura] = await Promise.all([
      supabase
        .from("credit_v2_user_tenants")
        .select("*, tenant:credit_v2_tenants(*)")
        .eq("user_id", user.id),
      unidadesLidas(),
    ])

    const vinculadas: Tenant[] = (data ?? [])
      .map((ut: { tenant: Tenant | null }) => ut.tenant)
      .filter((t: Tenant | null): t is Tenant => t !== null)
      // #222: ocultar ESCs desativadas
      .filter((t: Tenant) => !t.deactivated_at)
    const userTenants = juntarUnidadesLidas(vinculadas, leitura)
    setTenants(userTenants)

    const target = selectTenantId
      ? userTenants.find((t) => t.id === selectTenantId)
      : null
    if (target) {
      const decidido = escopoPedido(target.id, userTenants)
      setCurrentTenantState(decidido.unidade)
      setSelectedScopeRaw(decidido.escopo)
      if (decidido.gravar) {
        localStorage.setItem(`creditos_scope_${user.id}`, decidido.escopo)
      }
    } else {
      const unica = unidadeUnica(userTenants)
      if (unica) {
        // Ficou com uma unidade só: o escopo é ela, na mesma atualização que
        // troca a lista. Imposta, não escolhida, então não é gravada.
        setCurrentTenantState(unica)
        setSelectedScopeRaw(unica.id)
      } else {
        // Nenhuma unidade, ou duas ou mais: garante que algo fique em
        // currentTenant, sem mexer no escopo.
        setCurrentTenantState((prev) => prev ?? userTenants[0] ?? null)
      }
    }
  }, [user])

  const viewAll = selectedScope === "all"
  const activeTenantId = viewAll ? currentTenant?.id : selectedScope

  const tenantMap = useMemo(() => {
    const map: Record<string, string> = {}
    tenants.forEach((t) => { map[t.id] = t.name })
    return map
  }, [tenants])

  // task #13.23 — alerta de cidade fora da área (sem bloqueio).
  const tenantCitiesMap = useMemo(() => {
    const map: Record<string, string[]> = {}
    tenants.forEach((t) => { map[t.id] = t.allowed_cities ?? [] })
    return map
  }, [tenants])

  const activeTenantColor = useMemo(() => {
    if (viewAll) return null
    const t = tenants.find((t) => t.id === selectedScope)
    return t?.primary_color ?? null
  }, [viewAll, selectedScope, tenants])

  const contextValue: TenantContextValue = {
    user,
    profile,
    role,
    isLoading,
    isDataReady,
    tenants,
    currentTenant,
    isInGodMode,
    godModeTenant,
    setCurrentTenant,
    enterGodMode,
    exitGodMode,
    refreshProfile,
    refreshTenants,
    signOut,
    can,
    hasRole,
    isAtLeast,
    hasLoadError,
    retryLoad,
    selectedScope,
    setSelectedScope,
    viewAll,
    activeTenantId,
    tenantMap,
    tenantCitiesMap,
    activeTenantColor,
  }

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>
}

// ─── Hook ──────────────────────────────────────────────────────────────────────
export function useTenantContext(): TenantContextValue {
  const ctx = useContext(TenantContext)
  if (!ctx) {
    throw new Error("useTenantContext deve ser usado dentro de TenantProvider")
  }
  return ctx
}

/**
 * Hook de conveniencia para acessar o tenant atual.
 * Lanca erro se usado fora do contexto ou sem tenant selecionado.
 */
export function useCurrentTenant(): Tenant {
  const { currentTenant } = useTenantContext()
  if (!currentTenant) {
    throw new Error("Nenhum tenant selecionado.")
  }
  return currentTenant
}
